import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const Background = () => {
  const canvasRef = useRef(null);
  const location = useLocation();

  // Determine theme based on path
  const getTheme = (path) => {
    if (path.includes('vulnerability')) return 'vulnerability';
    if (path.includes('risk')) return 'risk';
    if (path.includes('penetration')) return 'penetration';
    if (path.includes('cmmc')) return 'compliance';
    if (path.includes('pci')) return 'compliance';
    if (path.includes('audit')) return 'audit';
    return 'default';
  };

  const theme = getTheme(location.pathname);

  // Theme color configurations
  const themes = {
    default: {
      r: 0, g: 255, b: 255, // Cyan
      bg: '#0a0a0a'
    },
    vulnerability: {
      r: 255, g: 100, b: 50, // Orange/Red
      bg: '#0f0505'
    },
    risk: {
      r: 255, g: 215, b: 0, // Gold
      bg: '#0f0f05'
    },
    penetration: {
      r: 255, g: 50, b: 50, // Red
      bg: '#0f0000'
    },
    compliance: {
      r: 0, g: 255, b: 100, // Green
      bg: '#000f05'
    },
    audit: {
      r: 150, g: 50, b: 255, // Purple
      bg: '#0a000f'
    }
  };

  const currentTheme = themes[theme] || themes.default;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];

    let mouse = { x: null, y: null };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const handleMouseMove = (event) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
    };

    const handleMouseLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };

    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseout', handleMouseLeave);

    resizeCanvas();

    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 2 + 1;
        this.baseX = this.x;
        this.baseY = this.y;
        this.density = (Math.random() * 30) + 1;
        // Use theme colors
        this.color = `rgba(${currentTheme.r}, ${currentTheme.g}, ${currentTheme.b}, ${Math.random() * 0.5 + 0.2})`;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Mouse interaction
        if (mouse.x != null) {
          let dx = mouse.x - this.x;
          let dy = mouse.y - this.y;
          let distance = Math.sqrt(dx * dx + dy * dy);
          const forceDirectionX = dx / distance;
          const forceDirectionY = dy / distance;
          const maxDistance = 150;
          const force = (maxDistance - distance) / maxDistance;
          const directionX = forceDirectionX * force * this.density;
          const directionY = forceDirectionY * force * this.density;

          if (distance < maxDistance) {
            // Repel
            this.x -= directionX;
            this.y -= directionY;
          }
        }

        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
    }

    const init = () => {
      particles = [];
      for (let i = 0; i < 100; i++) {
        particles.push(new Particle());
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw connections
      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach(p2 => {
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            // Use theme colors for connections
            ctx.strokeStyle = `rgba(${currentTheme.r}, ${currentTheme.g}, ${currentTheme.b}, ${1 - distance / 150})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
          }
        });

        // Connect to mouse
        if (mouse.x != null) {
          const dx = p1.x - mouse.x;
          const dy = p1.y - mouse.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${currentTheme.r}, ${currentTheme.g}, ${currentTheme.b}, ${1 - distance / 150})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }
      });

      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseout', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, [currentTheme]); // Re-run when theme changes

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        background: currentTheme.bg,
        transition: 'background 1s ease', // Smooth transition between themes
      }}
    />
  );
};

export default Background;
